/**
 * The annunciator strip: a fixed row of status lights, dark unless something is true.
 *
 * WHY THIS AND NOT ATMOSPHERE. He asked for a sci-fi command deck and named "obviously
 * AI-generated" as an anti-reference, which rules out the whole first shelf of the idea: neon on
 * black, glow, scanlines, animated grid. Those read as a poster of a control room rather than a
 * control room. What an actual instrument panel has that a dashboard usually lacks is DARK-COCKPIT
 * PHILOSOPHY: a fixed set of annunciators in learned positions, unlit in normal operation, so a
 * dark panel means nothing is wrong and a lit one is worth crossing the room for.
 *
 * That is also the thing his one-minute check has been missing. Every panel on the deck shows a
 * count, so the board is always full of numbers and none of them says "nothing needs you". This
 * strip answers that in one glance, and it is the same lesson as the attention feed: a system that
 * only ever raises, and never says all-clear, stops being read.
 *
 * THREE RULES, and the design is worth nothing without them.
 *
 * 1. FIXED SET, FIXED ORDER, ALWAYS PRESENT. An annunciator that appears only when lit teaches him
 *    nothing and moves the layout underneath his thumb. Unlit ones stay visible and dim so their
 *    positions become learnable, which is the entire reason a real panel works at a glance.
 *
 * 2. TWO TIERS, NOT SEVEN HUES. Caution means something is late or broken; advisory means
 *    something wants a decision. The deck's seven meaning-hues are right on panels, where hue
 *    encodes the KIND of thing, and wrong here, where a row of seven colours would be a
 *    decoration rather than a reading.
 *
 * 3. NOTHING LIT WITHOUT EVIDENCE, and "never configured" is not "stale". `mailLastRun: null` means
 *    ingestion has never run, which is a different claim from a run that has gone quiet, and the
 *    strip says which. That is the product's one non-negotiable principle applied at the pixel
 *    level: it never implies it knows something it does not.
 *
 * No pulse, no blink. A lit annunciator is static. Motion here would be decoration, and on a
 * surface he checks half-awake it would draw the eye to whichever light moved rather than to
 * whichever light matters.
 */

import type { JSX } from 'react';
import type { Dashboard } from '@rbv/shared';

/** How long a poller may be quiet before the strip calls it stale. */
const STALE_MINUTES = { mail: 6 * 60, calendar: 36 * 60 } as const;

export type Tier = 'caution' | 'advisory';

export interface Annunciator {
  /** Short enough to read at a glance, and it never changes with state. */
  label: string;
  tier: Tier;
  lit: boolean;
  /** What lit it, or why it is dark. Shown on hover and to a screen reader. */
  detail: string;
}

function minutesSince(iso: string | null, now: number): number | null {
  if (iso === null) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.round((now - t) / 60_000));
}

/**
 * Derive the strip from the payload. Pure, so the honesty rules are testable without a browser.
 *
 * Every signal here is counted from something the payload actually carries. Nothing is inferred
 * from an absence, with the single deliberate exception of the two pollers, where an absence IS the
 * signal and is labelled as "never run" rather than as stale.
 */
export function annunciators(d: Dashboard, now = Date.now()): Annunciator[] {
  const mail = minutesSince(d.telemetry.mailLastRun, now);
  const cal = minutesSince(d.telemetry.calendarLastSeen, now);

  return [
    {
      label: 'Overdue',
      tier: 'caution',
      lit: d.lanes.overdue > 0,
      detail:
        d.lanes.overdue > 0
          ? `${d.lanes.overdue} commitments past their due date`
          : 'Nothing past its due date',
    },
    {
      label: 'Mail',
      tier: 'caution',
      // Never having run is not the same failure as having stopped, and conflating them would
      // have him hunting a broken poller that was simply never switched on.
      lit: mail === null || mail > STALE_MINUTES.mail,
      detail:
        mail === null
          ? 'Mail ingestion has never run'
          : mail > STALE_MINUTES.mail
            ? `Last mail drain was ${Math.floor(mail / 60)}h ago`
            : `Mail drained ${mail}m ago`,
    },
    {
      label: 'Calendar',
      tier: 'caution',
      lit: cal === null || cal > STALE_MINUTES.calendar,
      detail:
        cal === null
          ? 'Calendar has never been read'
          : cal > STALE_MINUTES.calendar
            ? `Last calendar read was ${Math.floor(cal / 60)}h ago`
            : `Calendar read ${cal < 60 ? `${cal}m` : `${Math.floor(cal / 60)}h`} ago`,
    },
    {
      label: 'Judgement',
      tier: 'advisory',
      lit: d.proposals.length > 0,
      detail:
        d.proposals.length > 0
          ? `${d.proposals.length} questions waiting on you`
          : 'No questions waiting',
    },
    {
      label: 'Gaps',
      tier: 'advisory',
      lit: d.gaps.length > 0,
      detail: d.gaps.length > 0 ? `${d.gaps.length} things drifting` : 'Nothing flagged as drifting',
    },
  ];
}

export function AnnunciatorStrip({ dashboard }: { dashboard: Dashboard }): JSX.Element {
  const items = annunciators(dashboard);
  const lit = items.filter((a) => a.lit);

  return (
    <div className="annun" role="group" aria-label="System status">
      <ul className="annun-row">
        {items.map((a) => (
          <li
            key={a.label}
            className="annun-lamp"
            data-lit={a.lit}
            data-tier={a.tier}
            title={a.detail}
          >
            {/*
              The label is the light. No separate LED dot beside it: a dot plus a word says the
              same thing twice, and the word is the part that survives being colour-blind.
            */}
            <span className="annun-label">{a.label}</span>
            {/* Screen readers get the reason, not just the word. */}
            <span className="mo-sr-only">
              {a.lit ? 'attention: ' : 'clear: '}
              {a.detail}
            </span>
          </li>
        ))}
      </ul>
      {/*
        The all-clear line. Stated rather than implied, because an all-dark strip is only
        meaningful once you know it is supposed to be dark, and he will read this surface for the
        first time on a day when it happens to be full.
      */}
      <p className="annun-summary" data-clear={lit.length === 0}>
        {lit.length === 0
          ? 'Nothing flagged'
          : `${lit.length} flagged: ${lit.map((a) => a.label.toLowerCase()).join(', ')}`}
      </p>
    </div>
  );
}
